import React from 'react';
import {
  MonoKicker,
  GoldRule,
  PosterHeadline,
  SerifStory,
  Section,
  SectionMarker,
  Reveal,
  RevealHairline,
  Device,
  APP_GLOBE_CHICAGO,
} from '../index';

/**
 * §01 THE CROSSING — "800 feet apart. Six years too early."
 *
 * The hook works by withholding then landing. The ledger earns the number,
 * so the number arrives after it, not before.
 *
 * Every fact traces to REAL_STORY_FACTS.md:
 *   Chicago / New York, never the same city · six years (2018–2024)
 *   800 ft, Chicago, June 2019 — the only distance figure permitted on this
 *   site · nine hours, Scottsdale, May 2020 · under 24 hours, Tulum
 *   met January 18 2025 · engaged June 4 2025 · 460 crossings
 *
 * The payoff line once read "in a city neither of them lived in". That is
 * FALSE — Chicago was Nick's city; Michelle was visiting from New York. The
 * shipped line names the ache instead of the geography. Do not reintroduce
 * the geography variant, however well it scans.
 *
 * No CTA here. This band builds; the Showcase after it converts.
 *
 * Gold budget, walked at 390 in ~740px slices — one discretionary gold per
 * screenful. Structural gold (the outline line, the kicker rule) does not
 * count. Slice 1: none. Slice 2: the 800 FEET APART ledger value. Slice 3:
 * the numeral. Slice 4: none — June 4 2025 is coral. The payoff clause is
 * deliberately paper, not gold: it sits directly under a glowing gold
 * numeral, where a second gold would be redundant emphasis competing with
 * the exact moment it is meant to land.
 */

const LEDGER = [
  { label: 'Chicago · June 2019', value: '800 feet apart', tone: 'gold' },
  { label: 'Scottsdale · May 2020', value: 'Nine hours apart', tone: 'paper' },
  { label: 'Tulum, Mexico', value: 'Less than 24 hours apart', tone: 'paper' },
];

/** Ledger row with its own drawing hairline — the case file assembling. */
/**
 * Ledger row.
 *
 * This was <dl> → div → div → div → <dt>/<dd>. A definition list only permits
 * dt/dd as direct children, or wrapped in a SINGLE div; three levels deep
 * orphans every one of them, which is what the dlitem/definition-list audits
 * were firing on. The dot-leader span between dt and dd broke the content
 * model too — dl>div must be dt* followed by dd*, with nothing in between.
 *
 * Rebuilt as plain divs and spans. The reading order and the announced text
 * are identical, and the markup is unambiguously valid; the list carries its
 * meaning from the label/value pairing and the section heading above it, not
 * from dl semantics that could not survive the layout anyway.
 */
function LedgerRow({ label, value, tone, delay }) {
  const tones = { gold: 'text-gold-400', paper: 'text-paper' };
  return (
    <div className="w-full">
      <Reveal delay={delay}>
        <div className="flex flex-col items-start gap-1 pb-[10px] min-[421px]:flex-row min-[421px]:items-baseline min-[421px]:gap-0">
          <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">{label}</span>
          <span
            aria-hidden="true"
            className="hidden min-w-[24px] flex-1 -translate-y-[0.28em] mx-3 border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
          />
          <span
            className={`cp-mono ml-6 text-[length:var(--fs-mono-value)] min-[421px]:ml-0 ${tones[tone]}`}
          >
            {value}
          </span>
        </div>
      </Reveal>
      <RevealHairline delay={delay} />
    </div>
  );
}

export function Story() {
  return (
    <Section id="story" labelledBy="story-h" tone="canvas" className="overflow-x-clip">
      <SectionMarker className="mb-10">§ 01 · The Crossing</SectionMarker>

      {/* items-center at lg: the exhibit column is taller than the type
          column, and left-aligning both dumps all the slack under the ledger
          as one dead 400px hole. Centring distributes it. */}
      <div className="grid gap-14 lg:grid-cols-12 lg:items-center lg:gap-10">
        {/* ---- type column: 7 of 12, never 6/6 ---- */}
        <div className="lg:col-span-7">
          <Reveal>
            <GoldRule />
            <MonoKicker className="mt-2">THE FOUNDERS' STORY</MonoKicker>
            <PosterHeadline
              as="h2"
              id="story-h"
              size="section"
              className="mt-3.5"
              lines={[{ text: 'Six years' }, { text: 'of near misses', outline: true }]}
            />
          </Reveal>

          <Reveal delay={120}>
            <SerifStory className="mt-[18px] max-w-[52ch]">
              Nick lived in Chicago. Michelle lived in New York. They had never lived in the
              same city — and for six years, neither of them knew the other existed.
            </SerifStory>
          </Reveal>

          {/* ---- the evidence ledger ---- */}
          <Reveal delay={200} className="mt-12">
            <MonoKicker>THE EVIDENCE</MonoKicker>
          </Reveal>
          <div className="mt-4" data-testid="story-ledger">
            {LEDGER.map((row, i) => (
              <LedgerRow key={row.label} {...row} delay={240 + i * 90} />
            ))}
          </div>
        </div>

        {/* ---- exhibit: one device, straight, no glow on the frame ---- */}
        <div className="lg:col-span-4 lg:col-start-9">
          <Reveal delay={880} className="flex flex-col items-start lg:items-center">
            <Device
              src={APP_GLOBE_CHICAGO.src}
              alt={APP_GLOBE_CHICAGO.alt}
              width={APP_GLOBE_CHICAGO.width}
              height={APP_GLOBE_CHICAGO.height}
              className="w-[240px] md:w-[260px] lg:w-[270px]"
            />
            <p className="cp-mono mt-4 max-w-[270px] text-[length:var(--fs-mono)]">
              Their real map · 460 crossings · 2018—2024
            </p>
          </Reveal>
        </div>
      </div>

      {/* ---- the numeral: the page's single biggest typographic moment ---- */}
      <div className="mt-16 lg:mt-24">
        <Reveal delay={480}>
          <MonoKicker>CLOSEST THEY EVER CAME</MonoKicker>
        </Reveal>
        <Reveal delay={560} duration={800} className="mt-2">
          {/* 800 and FEET live in one element so it is announced as "800 feet",
              not as two unrelated blocks. Real selectable text, never an image. */}
          <p
            data-testid="story-numeral"
            className="cp-poster cp-numeral-glow text-gold-400 text-[length:var(--fs-numeral)] leading-[0.82]"
            style={{ marginLeft: '-0.06em' }}
          >
            800
            <span className="ml-[0.14em] align-baseline text-[0.3em] tracking-[0.02em] text-paper">
              feet
            </span>
          </p>
        </Reveal>

        <Reveal delay={760}>
          <SerifStory className="mt-8 max-w-[46ch]">
            Eight hundred feet apart, six years before either of them{' '}
            <em className="italic text-paper">knew the other existed.</em>
          </SerifStory>
        </Reveal>
      </div>

      {/* ---- closing rows ---- */}
      <Reveal delay={880} className="mt-14">
        {/* Divs and spans, not dl/dt/dd — the dot-leader span sits between
            label and value, which a definition list's content model does not
            permit, and the wrapper depth orphaned the dt/dd besides. */}
        <div className="grid gap-y-3 md:grid-cols-2 md:gap-x-16">
          <div className="flex items-baseline">
            <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">Met</span>
            <span
              aria-hidden="true"
              className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
            />
            <span className="cp-mono ml-auto text-[length:var(--fs-mono-value)] text-paper min-[421px]:ml-0">
              January 18, 2025
            </span>
          </div>
          <div className="flex items-baseline">
            <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">Engaged</span>
            <span
              aria-hidden="true"
              className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
            />
            <span className="cp-mono ml-auto text-[length:var(--fs-mono-value)] text-coral min-[421px]:ml-0">
              June 4, 2025
            </span>
          </div>
        </div>
      </Reveal>
      <RevealHairline delay={920} className="mt-3" />
    </Section>
  );
}

export default Story;
